import { useMemo, useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { ClientCombobox } from "@/components/ClientCombobox";
import { toast } from "sonner";
import { X, FilePdf, Copy, PaperPlaneTilt } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/lib/money";

const SCOPES = [
  { key: "outstanding", label: "Outstanding invoices" },
  { key: "paid", label: "Paid invoices" },
  { key: "all", label: "All invoices" },
];

const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const usd = money;

const COLUMNS = [
  { key: "number", label: "Invoice #", money: false },
  { key: "date", label: "Date", money: false },
  { key: "week_ending", label: "Week Ending", money: false },
  { key: "project", label: "Project", money: false },
  { key: "po", label: "PO #", money: false },
  { key: "status", label: "Status", money: false },
  { key: "total", label: "Total", money: true },
  { key: "paid", label: "Paid", money: true },
  { key: "balance", label: "Balance", money: true },
];
const DEFAULT_COLS = ["week_ending", "po", "balance"];

function cellVal(r, key) {
  switch (key) {
    case "number": return r.number || "";
    case "date": return r.invoice_date || "";
    case "week_ending": return r.week_ending || "";
    case "project": return r.project_name || "";
    case "po": return r.po_number || "";
    case "status": return (r.status || "").replace(/_/g, " ").toUpperCase() + (r.days_overdue ? ` (${r.days_overdue}d)` : "");
    default: return usd(r[key]);
  }
}

function buildStatementHtml(d, colKeys) {
  const cols = COLUMNS.filter((c) => colKeys.includes(c.key));
  const co = d.company || {};
  const filt = [];
  if (d.project) filt.push(`Project: ${esc(d.project.name)}`);
  if (d.date_from || d.date_to) filt.push(`Weeks ending ${d.date_from || "…"} to ${d.date_to || "…"}`);
  const tdStyle = (c) => `padding:6px 8px;border:1px solid #cbd5e1${c.money ? ";text-align:right" : ""}${c.key === "number" ? ";font-family:monospace" : ""}${c.key === "balance" ? ";font-weight:bold" : ""}`;
  const rows = d.rows.map((r, i) =>
    `<tr style="background:${i % 2 ? "#f8fafc" : "#ffffff"}">${cols.map((c) => `<td style="${tdStyle(c)}">${esc(cellVal(r, c.key))}</td>`).join("")}</tr>`
  ).join("");
  const th = (t, right) => `<th style="padding:6px 8px;border:1px solid #0f172a;background:#0f172a;color:#ffffff;text-align:${right ? "right" : "left"};font-size:12px">${t}</th>`;
  const nLabel = cols.filter((c) => !c.money).length;
  const totCell = "padding:6px 8px;border:1px solid #0f172a";
  const labelCell = nLabel > 0
    ? `<td colspan="${nLabel}" style="${totCell}">TOTALS (${d.totals.count} invoice${d.totals.count === 1 ? "" : "s"})</td>`
    : "";
  const moneyCells = cols.filter((c) => c.money)
    .map((c) => `<td style="${totCell};text-align:right">${usd(d.totals[c.key])}</td>`).join("");
  const a = d.aging || {};
  const agingLine = d.totals.balance > 0
    ? `<p style="font-size:12px;color:#334155;margin:10px 0 0"><b>Aging:</b> Current ${usd(a.current)} &nbsp;·&nbsp; 1–30d ${usd(a.d1_30)} &nbsp;·&nbsp; 31–60d ${usd(a.d31_60)} &nbsp;·&nbsp; 61–90d ${usd(a.d61_90)} &nbsp;·&nbsp; 90d+ ${usd(a.d90_plus)}</p>`
    : "";
  return `<div style="font-family:Arial,Helvetica,sans-serif;color:#0f172a;max-width:760px">
  <h2 style="margin:0 0 2px;font-size:18px">${esc(co.name)}</h2>
  <p style="margin:0;color:#475569;font-size:12px">${esc((co.address_lines || []).join(" · "))}${co.phone ? ` · ${esc(co.phone)}` : ""}${co.email ? ` · ${esc(co.email)}` : ""}</p>
  <div style="height:3px;background:#22c55e;margin:10px 0"></div>
  <h3 style="margin:0 0 4px;font-size:16px">STATEMENT OF ACCOUNT — ${esc(d.scope_label)}</h3>
  <p style="margin:0 0 2px;font-size:13px">For: <b>${esc(d.client.name)}</b> &nbsp;·&nbsp; As of ${esc(d.as_of)}</p>
  ${filt.length ? `<p style="margin:0;font-size:12px;color:#64748b">${filt.join(" · ")}</p>` : ""}
  <table style="border-collapse:collapse;width:100%;font-size:12px;margin-top:10px">
    <thead><tr>${cols.map((c) => th(c.label, c.money)).join("")}</tr></thead>
    <tbody>${rows}
      <tr style="background:#ecfdf5;font-weight:bold">${labelCell}${moneyCells}</tr>
    </tbody>
  </table>
  ${agingLine}
  <p style="font-size:12px;color:#334155;margin-top:12px">Please include invoice number(s) with your payment. Questions: ${esc(co.email || "")}${co.phone ? ` · ${esc(co.phone)}` : ""}</p>
</div>`;
}

function buildStatementText(d, colKeys) {
  const cols = COLUMNS.filter((c) => colKeys.includes(c.key));
  const lines = [
    `${d.company?.name || ""} — STATEMENT OF ACCOUNT (${d.scope_label})`,
    `For: ${d.client.name} · As of ${d.as_of}`,
    "",
  ];
  for (const r of d.rows) {
    lines.push(cols.map((c) => `${c.label}: ${cellVal(r, c.key)}`).join("  |  "));
  }
  const totBits = cols.filter((c) => c.money).map((c) => `${c.label} ${usd(d.totals[c.key])}`);
  lines.push("", `TOTALS (${d.totals.count}): ${totBits.join(" · ") || `Balance ${usd(d.totals.balance)}`}`);
  return lines.join("\n");
}

export default function StatementModal({ clients, projects, onClose }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [clientId, setClientId] = useState("");
  const [scope, setScope] = useState("outstanding");
  const [projectId, setProjectId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [emailNote, setEmailNote] = useState("");
  const [sending, setSending] = useState(false);
  const previewRef = useRef(null);
  const [colKeys, setColKeys] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("sos-statement-cols") || "null");
      if (Array.isArray(raw) && raw.length) return COLUMNS.map((c) => c.key).filter((k) => raw.includes(k));
    } catch { /* corrupt json — fall through */ }
    return DEFAULT_COLS;
  });
  const toggleCol = (key) => {
    setColKeys((prev) => {
      if (prev.includes(key)) {
        if (prev.length === 1) { toast.error("Keep at least one column."); return prev; }
        const next = prev.filter((k) => k !== key);
        localStorage.setItem("sos-statement-cols", JSON.stringify(next));
        return next;
      }
      const next = COLUMNS.map((c) => c.key).filter((k) => prev.includes(k) || k === key);
      localStorage.setItem("sos-statement-cols", JSON.stringify(next));
      return next;
    });
  };

  const clientProjects = useMemo(
    () => (projects || []).filter((p) => p.client_id === clientId),
    [projects, clientId]
  );

  const params = () => {
    const q = new URLSearchParams({ client_id: clientId, scope });
    if (projectId) q.set("project_id", projectId);
    if (dateFrom) q.set("date_from", dateFrom);
    if (dateTo) q.set("date_to", dateTo);
    return q;
  };

  const generate = async () => {
    if (!clientId) { toast.error("Pick a client first."); return; }
    setBusy(true);
    try {
      const { data: d } = await api.get(`/invoices/statement?${params().toString()}`);
      setData(d);
      setEmailTo(d.client?.billing_email || "");
      setEmailOpen(false);
      if (!d.rows.length) toast.info("No invoices match those selections.");
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || e.message);
    } finally { setBusy(false); }
  };

  const html = useMemo(() => (data ? buildStatementHtml(data, colKeys) : ""), [data, colKeys]);

  const sendEmail = async (e) => {
    e.preventDefault();
    if (!emailTo.trim()) { toast.error("Enter a recipient email."); return; }
    setSending(true);
    try {
      const { data: r } = await api.post("/invoices/statement/email", {
        client_id: clientId, scope, project_id: projectId || null, date_from: dateFrom || null, date_to: dateTo || null,
        cols: colKeys.join(","), to: emailTo.trim(), cc: emailCc.trim() || null, note: emailNote.trim() || null,
      });
      toast.success(`Statement emailed to ${r.to.join(", ")} — ${r.invoices} invoice${r.invoices === 1 ? "" : "s"}, balance ${usd(r.balance)}.`);
      setEmailOpen(false);
    } catch (e2) { toast.error(e2.response?.data?.detail?.toString() || e2.message, { duration: 8000 }); }
    finally { setSending(false); }
  };

  const copyForEmail = async () => {
    try {
      await navigator.clipboard.write([
        new window.ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([buildStatementText(data, colKeys)], { type: "text/plain" }),
        }),
      ]);
      toast.success("Statement copied — paste it straight into your email.");
    } catch {
      try {
        const el = previewRef.current;
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        document.execCommand("copy");
        sel.removeAllRanges();
        toast.success("Statement copied — paste it straight into your email.");
      } catch {
        toast.error("Couldn't access the clipboard — select the preview and copy manually.");
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl my-4 sm:my-8 max-h-[92vh] overflow-y-auto"
           data-testid="statement-modal">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold">Client statement</h2>
          <button data-testid="statement-close-btn" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={22} /></button>
        </div>

        <div className="px-6 py-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Client *</label>
              <div className="mt-1" data-testid="statement-client-picker">
                <ClientCombobox value={clientId} clients={clients} testId="statement-client-combobox"
                  onChange={(v) => { setClientId(v); setProjectId(""); setData(null); }} />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">What to include</label>
              <select data-testid="statement-scope-select" value={scope}
                onChange={(e) => { setScope(e.target.value); setData(null); }}
                className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md bg-white">
                {SCOPES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Project / job (optional)</label>
              <select data-testid="statement-project-select" value={projectId}
                onChange={(e) => { setProjectId(e.target.value); setData(null); }}
                disabled={!clientId}
                className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md bg-white disabled:bg-slate-50 disabled:text-slate-400">
                <option value="">All projects</option>
                {clientProjects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Weeks ending from</label>
                <input type="date" data-testid="statement-from-input" value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setData(null); }}
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">To</label>
                <input type="date" data-testid="statement-to-input" value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setData(null); }}
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-2">Drafts and voided invoices are never included on a statement.</p>

          <div className="mt-3">
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Columns to include</label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {COLUMNS.map((c) => {
                const on = colKeys.includes(c.key);
                return (
                  <button key={c.key} type="button" data-testid={`statement-col-${c.key}`}
                    onClick={() => toggleCol(c.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${on ? "bg-green-600 border-green-600 text-white" : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
                    {c.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-400 mt-1.5">Your column choices are remembered and apply to the preview, the email copy, and the PDF.</p>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button data-testid="statement-generate-btn" onClick={generate} disabled={busy || !clientId}
              className="h-11 px-5 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md disabled:opacity-50">
              {busy ? "Generating…" : "Generate statement"}
            </button>
            {data && data.rows.length > 0 && (
              <>
                <button data-testid="statement-copy-btn" onClick={copyForEmail}
                  className="h-11 px-4 border-2 border-green-600 text-green-700 font-semibold rounded-md hover:bg-green-50 flex items-center gap-2">
                  <Copy size={18} weight="bold" /> Copy for email
                </button>
                <a data-testid="statement-download-pdf-btn"
                  href={`${API_BASE}/invoices/statement?${params().toString()}&cols=${colKeys.join(",")}&format=pdf`}
                  className="h-11 px-4 border border-slate-300 rounded-md font-semibold hover:bg-slate-50 flex items-center gap-2 text-slate-700">
                  <FilePdf size={18} weight="bold" /> Download PDF
                </a>
                {isAdmin && (
                  <button data-testid="statement-email-btn" onClick={() => setEmailOpen((v) => !v)}
                    className={`h-11 px-4 rounded-md font-semibold flex items-center gap-2 ${emailOpen ? "bg-slate-900 text-white" : "bg-green-600 hover:bg-green-700 text-white"}`}>
                    <PaperPlaneTilt size={18} weight="bold" /> Email statement
                  </button>
                )}
              </>
            )}
          </div>

          {emailOpen && data && data.rows.length > 0 && (
            <form onSubmit={sendEmail} className="mt-4 border border-green-200 bg-green-50/60 rounded-md p-4" data-testid="statement-email-form">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-600 mb-2">Email this statement (PDF attached + summary in the body)</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">To
                  <input type="text" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} data-testid="statement-email-to" placeholder="payables@client.com"
                    className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md font-normal normal-case tracking-normal" />
                  <span className="block mt-1 text-[11px] font-normal normal-case tracking-normal text-slate-500">
                    {data.client?.billing_email
                      ? (emailTo.trim() !== data.client.billing_email.trim() ? <span className="text-amber-700 font-semibold">Different from the billing email on file ({data.client.billing_email}).</span> : "Defaults to the email invoices are sent to — change it if someone else asked for the statement.")
                      : <span className="text-amber-700 font-semibold">No billing email on file — enter one.</span>}
                  </span>
                </label>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">CC (optional)
                  <input type="text" value={emailCc} onChange={(e) => setEmailCc(e.target.value)} data-testid="statement-email-cc" placeholder="comma-separated"
                    className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md font-normal normal-case tracking-normal" />
                </label>
              </div>
              <label className="block mt-3 text-xs font-bold text-slate-500 uppercase tracking-wide">Personal note (optional)
                <textarea rows={2} value={emailNote} onChange={(e) => setEmailNote(e.target.value)} data-testid="statement-email-note" placeholder="e.g. As requested — statement covering March through June."
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md font-normal normal-case tracking-normal" />
              </label>
              <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                <span className="text-xs text-slate-500">{data.totals.count} invoice{data.totals.count === 1 ? "" : "s"} · balance {usd(data.totals.balance)} · office BCC'd</span>
                <button data-testid="statement-email-send" disabled={sending || !emailTo.trim()}
                  className="h-11 px-5 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md disabled:opacity-50 flex items-center gap-2">
                  <PaperPlaneTilt size={16} weight="bold" /> {sending ? "Sending…" : "Send statement"}
                </button>
              </div>
            </form>
          )}

          {data && (
            data.rows.length > 0 ? (
              <div className="mt-4 border border-slate-200 rounded-md p-4 overflow-x-auto bg-slate-50">
                <div ref={previewRef} data-testid="statement-preview"
                     dangerouslySetInnerHTML={{ __html: html }} />
              </div>
            ) : (
              <div data-testid="statement-empty" className="mt-4 border border-slate-200 rounded-md p-6 text-center text-sm text-slate-500">
                No invoices match those selections.
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
